/**
 * Pure parser for the forwarded-session tokens that saas-auth / saas-login
 * append to the `/auth/return` URL. Dependency-free and side-effect-free (no
 * `window`, no store writes) so it can be unit-tested in isolation.
 *
 * Two arrival shapes (see SASADM-0081 spec / backend contract §2.5, §2.7):
 *  - Direct SSO (no-MFA) — tokens in the QUERY string (the `appendTokens`
 *    shape, where `token` mirrors `accessToken`).
 *  - MFA hand-off — tokens in the URL FRAGMENT (so they never reach server
 *    logs/history). Operators always traverse MFA, so the fragment is the
 *    operative path; the fragment is preferred over the query when both carry
 *    an `accessToken`.
 */
export interface ReturnTokens {
  accessToken: string;
  refreshToken: string | null;
  expiresIn: number | null; // seconds
}

/**
 * Extract forwarded session tokens from a /auth/return arrival. Fragment
 * (MFA hand-off) is preferred over query (direct-SSO) when both carry an
 * accessToken. `token` is accepted as a query alias for `accessToken`
 * (the appendTokens shape). Returns null when no accessToken is present.
 */
export function parseReturnTokens(search: string, hash: string): ReturnTokens | null {
  const fromHash = readTokenParams(hash.replace(/^#/, ''));
  if (fromHash) return fromHash;
  return readTokenParams(search.replace(/^\?/, ''));
}

function readTokenParams(raw: string): ReturnTokens | null {
  if (!raw) return null;
  const p = new URLSearchParams(raw);
  const accessToken = p.get('accessToken') ?? p.get('token');
  if (!accessToken) return null;
  const refreshToken = p.get('refreshToken');
  const expiresInRaw = p.get('expiresIn');
  const n = expiresInRaw != null ? Number.parseInt(expiresInRaw, 10) : NaN;
  const expiresIn = Number.isFinite(n) && n > 0 ? n : null;
  return { accessToken, refreshToken, expiresIn };
}
